import { t } from "i18next";
import { Fragment } from "react/jsx-runtime";

import { useCompany } from "#modules/company/view/useCompany";
import { QuoteRequestLineOverthicknessDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLineOverthickness.api";
import { RawMaterialLayoutForm } from "#modules/quoteRequestLines/view/RawMaterialLayoutForm";
import { VolumetricMaterialLayoutForm } from "#modules/quoteRequestLines/view/VolumetricMaterialLayoutForm";
import { Modal } from "#shared/view/components/Modal";
import { MaterialGradeType, PartTypeType, PricingModeSchema, PricingModeType } from "shared";

import { QuoteRequestLineDto } from "../infra/fetchQuoteRequestLine.api";
import { useQuoteRequestLine } from "./useQuoteRequestLine";

export type { RawMaterialFormData } from "./RawMaterialLayoutForm";

type Props = {
  quoteRequestLineId: string | undefined;
  isOpened: boolean;
  close: () => void;
  cancel: () => void;
  materialGrade: MaterialGradeType;
  partType: PartTypeType | null;
  partSubType: string | null;
  overthickness: QuoteRequestLineOverthicknessDto;
  hasChildren: boolean;
};

export const MaterialLayoutModal = ({
  quoteRequestLineId,
  isOpened,
  close,
  cancel,
  materialGrade,
  partType,
  partSubType,
  overthickness,
  hasChildren,
}: Props) => {
  const { quoteRequestLine } = useQuoteRequestLine({ id: quoteRequestLineId });
  const { company } = useCompany();

  if (!quoteRequestLine) {
    return null;
  }

  const pricingMode =
    quoteRequestLine.layout?.materialLayout?.pricingMode ??
    company?.companySettings.materialPricingMode ??
    PricingModeSchema.enum.RAW;

  return (
    <MaterialLayoutModalContent
      quoteRequestLine={quoteRequestLine}
      close={close}
      cancel={cancel}
      isOpened={isOpened}
      materialGrade={materialGrade}
      partType={partType}
      partSubType={partSubType}
      overthickness={overthickness}
      hasChildren={hasChildren}
      pricingMode={pricingMode}
    />
  );
};

const MaterialLayoutModalContent = ({
  quoteRequestLine,
  close,
  cancel,
  isOpened,
  materialGrade,
  partType,
  partSubType,
  overthickness,
  hasChildren,
  pricingMode,
}: {
  quoteRequestLine: QuoteRequestLineDto;
  isOpened: boolean;
  close: () => void;
  cancel: () => void;
  materialGrade: MaterialGradeType;
  partType: PartTypeType | null;
  partSubType: string | null;
  overthickness: QuoteRequestLineOverthicknessDto;
  hasChildren: boolean;
  pricingMode: PricingModeType;
}) => {
  const isVolumetric = pricingMode === PricingModeSchema.enum.VOLUMETRIC;

  return (
    <Fragment>
      <Modal
        opened={isOpened}
        withCloseButton={false}
        onClose={cancel}
        size={800}
        title={t("material.updateMaterialLayoutModalTitle")}
      >
        {isVolumetric ? (
          <VolumetricMaterialLayoutForm
            quoteRequestLine={quoteRequestLine}
            close={close}
            cancel={cancel}
            materialGrade={materialGrade}
            partType={partType}
            hasChildren={hasChildren}
          />
        ) : (
          <RawMaterialLayoutForm
            quoteRequestLine={quoteRequestLine}
            close={close}
            cancel={cancel}
            materialGrade={materialGrade}
            partType={partType}
            partSubType={partSubType}
            overthickness={overthickness}
            hasChildren={hasChildren}
          />
        )}
      </Modal>
    </Fragment>
  );
};
